import { useEffect, useRef } from "react";

const SELECTOR = "[data-reveal],[data-lines],[data-rule],[data-img],[data-count]";

/** Counts the first number inside a figure up once, keeping prefix/suffix text. */
function runCount(el: HTMLElement) {
  const text = el.dataset.count || el.textContent || "";
  const m = text.match(/[\d,]+/);
  if (!m) return;
  const target = parseInt(m[0].replace(/,/g, ""), 10);
  const before = text.slice(0, m.index);
  const after = text.slice((m.index || 0) + m[0].length);
  const useComma = m[0].includes(",");
  const start = performance.now();
  const dur = 1100;
  const tick = (now: number) => {
    const t = Math.min(1, (now - start) / dur);
    const eased = 1 - Math.pow(1 - t, 4);
    const v = Math.round(target * eased);
    el.textContent = before + (useComma ? v.toLocaleString("en-GB") : String(v)) + after;
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/**
 * Arms editorial entrances (line masks, rules, image crops, counters) once each.
 * Content stays visible if JS, IntersectionObserver or motion is unavailable.
 */
export function useEditorialReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const items = Array.from(root.querySelectorAll<HTMLElement>(SELECTOR));
    const show = (el: HTMLElement) => {
      if (el.classList.contains("is-in")) return;
      el.classList.add("is-in");
      if (el.hasAttribute("data-count")) runCount(el);
    };
    root.classList.add("ed-armed");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        // reveal on entry; anything already scrolled past (e.g. anchor jumps) shows immediately
        if (e.isIntersecting || e.boundingClientRect.bottom < 0) { show(e.target as HTMLElement); io.unobserve(e.target); }
      }),
      { rootMargin: "0px 0px -10% 0px", threshold: 0 },
    );
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}
